import { createAppSlice } from "../../app/createAppSlice";
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit';
import { startAppListening } from "../../app/listenerMiddleware";
import getUsageData from "../../functions/getUsageDataAPI";
import { changedResourceAdded } from "../changedResourcesList/changedResourcesListSlice";
import { getAssociatedResourcesDictionary } from "../../functions/getResourceReferences";
import getResourceDictionaryFromFoiList from "../../functions/getResourceDictionaryFromFoiListAPI";
import addFoisFromFlis from "../../functions/addFoisFromFlisAPI";
import { selectCurrentUserApiToken, selectSseConnectionId } from "../currentUser/currentUserSlice";

const fetchResourceCollectionAPI = async (apiToken, connectionId, fhirJson, focusedResource = false) => {
    let resourceEntities = [];
    let resourceIdList = [];
    let resource = fhirJson;
    let entity = {
        id: resource.id,
        displayTitle: resource.title ||
            (typeof resource.name === "string" && resource.name) ||
            (Array.isArray(resource.name) && resource.name[0].text) ||
            "Unnamed " + resource.resourceType,
        resourceType: resource.resourceType,
        resourceJson: resource,
        revisedResourceJson: resource,
        usageData: {},
        permissionData: {},
        inviteCode: "",
        editors: []
    };
    resourceEntities.push(entity);

    //associatedDictionary[resourceType].push({ "title": string, "url": string, "foi": string, "fli": string });
    let associatedResourcesDictionary = getAssociatedResourcesDictionary(fhirJson);
    for (const resourceType in associatedResourcesDictionary) {
        const associatedResources = associatedResourcesDictionary[resourceType];
        let fliListToProcess = [];
        let theResources = associatedResources.map(associatedResource => {
            let entry;
            if (associatedResource.foi && !isNaN(associatedResource.foi)) {
                entry = {
                    id: associatedResource.foi,
                    title: associatedResource.title
                };
            } else if (associatedResource.fli) {
                entry = {
                    id: "",
                    title: associatedResource.title,
                    fli: associatedResource.fli
                };
                fliListToProcess.push(associatedResource.fli);
            }
            return entry;
        });
        if (fliListToProcess.length > 0) {
            let response = await addFoisFromFlis(apiToken, theResources);
            if (response?.success && response.foilist?.length && response.flitofoi) {
                for (let resource of theResources) {
                    if (!resource?.id || isNaN(resource.id)) {
                        if (resource?.fli && response.flitofoi[resource.fli]) {
                            resource.id = response.flitofoi[resource.fli];
                        } else {
                            resource.id = "";
                        }
                    }
                }
            }
        }
        for (const theResource of theResources) {
            if (theResource.id && !resourceIdList.includes(theResource.id)) {
                resourceIdList.push(theResource.id);
                let entity = {
                    id: theResource.id,
                    displayTitle: theResource.title || ("Unnamed " + resourceType),
                    resourceType: resourceType,
                    resourceJson: {},
                    revisedResourceJson: {},
                    usageData: {},
                    permissionData: {},
                    inviteCode: "",
                    editors: []
                };
                resourceEntities.push(entity);
            }
        }
    }
    let resourceDictionary = {};
    if (resourceIdList?.length > 0) {
        let resourceDictionaryResponse = await getResourceDictionaryFromFoiList(apiToken, connectionId, resourceIdList, focusedResource ? resource.id : null);
        if (resourceDictionaryResponse?.success && resourceDictionaryResponse.resourceDictionary) {
            //console.log("success getting resource Dictionary")
            resourceDictionary = resourceDictionaryResponse.resourceDictionary;
        } else {
            console.log("FAILURE getting resource Dictionary");
        }
    }

    resourceIdList.push(resource.id);
    //usageDataDictionary contains {totalviews: 0, totaledits: 0, totalusersviewed: 0, 
    //   totalusersedited: 0, editpermission: boolean, readpermission: boolean,
    //  adminpermission: boolean, status: active/draft/retired}
    let usageDataDictionary = await getUsageData(resourceIdList, apiToken);
    for (let entity of resourceEntities) {
        if (usageDataDictionary?.[entity.id]) {
            entity.usageData = {
                totalviews: usageDataDictionary[entity.id].totalviews,
                totaledits: usageDataDictionary[entity.id].totaledits,
                totalusersviewed: usageDataDictionary[entity.id].totalusersviewed,
                totalusersedited: usageDataDictionary[entity.id].totalusersedited
            };
            entity.permissionData = {
                editpermission: usageDataDictionary[entity.id].editpermission,
                readpermission: usageDataDictionary[entity.id].readpermission,
                adminpermission: usageDataDictionary[entity.id].adminpermission,
                status: usageDataDictionary[entity.id].status
            };
        }
        if (resourceDictionary?.[entity.id]) {
            entity.resourceJson = resourceDictionary[entity.id];
            entity.revisedResourceJson = resourceDictionary[entity.id];
        }
    }
    return resourceEntities;
}

//entity: { id: "FOI", displayTitle: "", 
//          resourceType: "", resourceJson: {}, revisedResourceJson: {}, 
//          usageData: {}, permissionData: {}, inviteCode: "", editors: [{ userid: userId, name: userName }] }
//key uses: Builder/Viewer Tools (to have access to full JSON of referenced Resources);

const resourceCollectionAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.resourceType && a?.resourceType && b.resourceType !== a.resourceType) {
            return b.resourceType.localeCompare(a.resourceType)
        } else if (b?.displayTitle && a?.displayTitle && b.displayTitle !== a.displayTitle) {
            return b.displayTitle.localeCompare(a.displayTitle)
        } else if (b?.id && a?.id && b.id.toString() !== a.id.toString()) {
            return b.id.toString().localeCompare(a.id.toString())
        } else {
            return 0
        }
    }
})

const initialState = resourceCollectionAdapter.getInitialState({
    status: 'idle',
    error: null
})

const resourceCollectionSlice = createAppSlice({
    name: "resourceCollection",
    initialState,
    reducers: create => ({
        resourceAdded: create.preparedReducer(
            (resourceBody) => {
                if (!resourceBody.resourceJson.meta) {
                    resourceBody.resourceJson.meta = {"versionId": "1"};
                }
                if (!resourceBody.resourceJson.meta.versionId) {
                    resourceBody.resourceJson.meta.versionId = "1";
                }
                return {
                    payload: {
                        id: resourceBody.id,
                        displayTitle: resourceBody.displayTitle,
                        resourceType: resourceBody.resourceType,
                        resourceJson: resourceBody.resourceJson,
                        revisedResourceJson: resourceBody.resourceJson,
                        usageData: resourceBody.usageData,
                        permissionData: resourceBody.permissionData,
                        inviteCode: resourceBody.inviteCode || "",
                        editors: resourceBody.editors || []
                    }
                }
            },
            resourceCollectionAdapter.addOne
        ),
        resourceUpdated: create.reducer((state, action) => {
            const { id, sourceOfChanges, ...changes } = action.payload; //Changes will have all the keys from payload other than "id", for example    action.payload = {"id": 123, "def": 456, "ghi": 789, "sourceOfChanges": 10 }, const id will equal 123, sourceOfChanges will equal 10, and changes will be {"def": 456, "ghi": 789 }}
            resourceCollectionAdapter.updateOne(state,
                { id, changes: changes })               //Will only update the keys passed to changes, so if any are missing it will just keep the old value
        }),
        usageDataUpdated: create.reducer((state, action) => {
            // The payload should be an object like: { id: "some-id", usageData: { ... } }
            const { id, usageData } = action.payload;
            resourceCollectionAdapter.updateOne(state, {
                id,
                changes: { usageData } // This will only update the usageData field
            });
        }),
        inviteCodeUpdated: create.reducer((state, action) => {
            // The payload should be: { id: "some-id", inviteCode: "new-code-xyz" }
            const { id, inviteCode } = action.payload;

            // Find the specific resource entity in the state
            const existingResource = state.entities[id];

            // If the resource exists, update its nested property
            if (existingResource) {
                existingResource.invitecode = inviteCode;
            }
        }),
        editorsUpdated: create.reducer((state, action) => {
            const { id, editors } = action.payload;
            const existingResource = state.entities[id];
            if (existingResource) {
                existingResource.editors = editors;
            }
        }),
        fetchResourceCollection: create.asyncThunk(
            async (body, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const connectionId = selectSseConnectionId(state);
                const resourceEntities = await fetchResourceCollectionAPI(apiToken, connectionId, body.fhirJson, true)
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { resourceCollection } = thunkApi.getState()
                         if (resourceCollection.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    resourceCollectionAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectResourceCollectionStatus: resourceCollection => resourceCollection.status,
        selectResourceCollectionError: resourceCollection => resourceCollection.error
    },
})

export const { resourceAdded, resourceUpdated, usageDataUpdated, inviteCodeUpdated, fetchResourceCollection, editorsUpdated } = resourceCollectionSlice.actions

export const { selectResourceCollectionStatus, selectResourceCollectionError } = resourceCollectionSlice.selectors

export const {
    selectAll: selectAllResources,
    selectById: selectResourceById,
} = resourceCollectionAdapter.getSelectors(state => state.resourceCollection)

export const addResourceCollectionListeners = () => {
    startAppListening({
        matcher: isAnyOf(resourceAdded, resourceUpdated),
        effect: (action, listenerApi) => {
            try {
                const resourceChange = action.payload;
                if (action.payload.sourceOfChanges !== "from-server") { //Or should this check occur further downstream?
                    listenerApi.dispatch(changedResourceAdded({ singleUpdate: resourceChange.id }));
                }
            } catch (err) {
                console.log("FAILURE of resourceCollection listener: ", err)
            }
        }
    })
}

export default resourceCollectionSlice